import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User as FirebaseUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import type { ActionCodeSettings } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase/config';
import { User, AuthState, LoginCredentials, SignupCredentials } from '../types';
import { DEFAULT_CURRENCY } from '../constants/currencies';
import { getPasswordResetContinueUrl } from '../utils/passwordResetUrl';
import { resolveUserLinkedApplicationId } from '../utils/applicationOwnership';
import { normalizeBccAddress } from '../utils/graph/complianceBcc';
import toast from 'react-hot-toast';

/** Saved compliance copy: one cleaned address, or null when empty or not a valid email. */
function savedComplianceBccAddress(raw: unknown): string | null {
  return normalizeBccAddress(raw) || null;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string; errorCode?: string }>;
  signup: (credentials: SignupCredentials) => Promise<{ success: boolean; error?: string; errorCode?: string }>;
  logout: () => Promise<void>;
  resetPassword: (email: string, redirectPath?: string) => Promise<void>;
  setUserRole: (role: User['role'], pin?: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Admin PIN validation moved to server-side Cloud Function (verifyAdminPin)
// See: functions/src/admin-verification.ts

// Helper function to get user-friendly error messages
const getAuthErrorMessage = (code: string): string => {
  switch (code) {
    // Security-conscious (do not reveal whether email exists)
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password. Please try again.';

    // Sign-up specific
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please login instead.';
    case 'auth/weak-password':
      return 'Password must be at least 8 characters with one uppercase letter and one number';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';

    // Network and system
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/unauthorized-continue-uri':
      return 'Password reset could not use this site address. Try again or contact support.';
    case 'auth/user-disabled':
      return 'Your account has been disabled. Please contact support.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled. Please try again.';

    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to create or update user document in Firestore
  const createOrUpdateUserDocument = async (firebaseUser: FirebaseUser, additionalData: Partial<User> = {}): Promise<User> => {
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // Creating new user document
        const roleForCreation = (additionalData.role as User['role']) || 'client';
        const baseData: any = {
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName || '',
          role: roleForCreation,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        // Only clients require approval. Admins and investors should NEVER need approval.
        const approvalSeed: any = roleForCreation === 'client'
          ? { approvalStatus: 'pending', approved: false }
          : {};

        await setDoc(userRef, { ...baseData, ...additionalData, ...approvalSeed });
        // Re-read to ensure Firestore Timestamps are materialized
        const createdSnap = await getDoc(userRef);
        return createdSnap.data() as User;
      } else {
        // Updating existing user document
        const updateData = {
          ...additionalData,
          updatedAt: serverTimestamp()
        };

        await updateDoc(userRef, updateData);
        const updatedSnap = await getDoc(userRef);
        return updatedSnap.data() as User;
      }
    } catch (error) {
      // Error creating/updating user document
      throw error;
    }
  };



    // Helper function to get user data from Firestore
  const getUserFromFirestore = async (firebaseUser: FirebaseUser): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Migrate existing users - add approval fields if missing
        let approvalData = {
          approved: userData.approved,
          approvalStatus: userData.approvalStatus,
          // Only include optional fields if they exist (avoid undefined values)
          ...(userData.approvedBy && { approvedBy: userData.approvedBy }),
          ...(userData.approvedAt && { approvedAt: userData.approvedAt }),
          ...(userData.rejectionReason && { rejectionReason: userData.rejectionReason })
        };
        
        if (userData.approved === undefined || userData.approvalStatus === undefined) {
          // Avoid setting approval fields for admins
          if (userData.role === 'admin' || userData.role === 'super_admin') {
            // No-op: admins should not have approval fields
          } else if (userData.role === 'client') {
            // Optionally backfill clients; keeping existing behavior to avoid broken states
            approvalData = {
              approved: true,
              approvalStatus: 'approved',
              approvedBy: 'system',
              approvedAt: new Date()
            };
            
            await updateDoc(doc(db, 'users', firebaseUser.uid), approvalData);
          }
        }
        
        const isTargetViewer = userData.role === 'target_viewer';
        const isProspectViewer = userData.role === 'prospect_viewer';
        const isRestrictedViewer = isTargetViewer || isProspectViewer;
        const finalUserData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || userData.displayName || '',
          role: userData.role,
          createdAt: (userData.createdAt as Timestamp) || Timestamp.fromDate(new Date()),
          lastLoginAt: (userData.lastLoginAt as Timestamp) || Timestamp.fromDate(new Date()),
          organizationId: userData.organizationId,
          // Profile fields
          photoURL: userData.photoURL || null,
          bio: userData.bio || '',
          calendarUrl: userData.calendarUrl || null,
          outlookComposeSignatureHtml: userData.outlookComposeSignatureHtml || null,
          outlookComplianceBccAddress: savedComplianceBccAddress(userData.outlookComplianceBccAddress),
          outlookComplianceBccUsOnly: Boolean(userData.outlookComplianceBccUsOnly),
          preferredCurrency: userData.preferredCurrency || DEFAULT_CURRENCY, // Currency preference (default from constants)
          // Owner linkage never applied for restricted viewers
          clientId: isRestrictedViewer
            ? undefined
            : resolveUserLinkedApplicationId(userData as any) || undefined,
          linkedApplicationId: isRestrictedViewer
            ? null
            : (userData as any).linkedApplicationId || null,
          targetViewerClientId: isTargetViewer
            ? (typeof userData.targetViewerClientId === 'string'
                ? userData.targetViewerClientId
                : undefined)
            : undefined,
          targetViewerPreviewClientId:
            !isRestrictedViewer && typeof userData.targetViewerPreviewClientId === 'string'
              ? userData.targetViewerPreviewClientId
              : undefined,
          prospectViewerClientId:
            typeof userData.prospectViewerClientId === 'string'
              ? userData.prospectViewerClientId
              : undefined,
          prospectViewerPreviewClientId:
            !isRestrictedViewer && typeof userData.prospectViewerPreviewClientId === 'string'
              ? userData.prospectViewerPreviewClientId
              : undefined,
          ...approvalData
        };
        
        return finalUserData;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  // Authentication methods
  const login = async (credentials: LoginCredentials): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
    try {
      // Logging in user
      const result = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      
      if (result.user) {
        // Login successful, auth state will load user data
        return { success: true };
      }
      
      return { success: false, error: 'Login failed' };
    } catch (error: any) {
      // Login failed
      const code = error?.code || 'unknown';
      const errorMessage = getAuthErrorMessage(code);
      toast.error(errorMessage);
      return { success: false, error: errorMessage, errorCode: code };
    }
  };

  const signup = async (credentials: SignupCredentials): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
    try {
      // Starting signup process
      const result = await createUserWithEmailAndPassword(auth, credentials.email, credentials.password);
      
      if (result.user) {
        // Firebase user created
        if (credentials.displayName) {
          // Updating Firebase profile with display name
          await updateProfile(result.user, { displayName: credentials.displayName });
        }
        
        // Creating Firestore user document
        const role = credentials.role || 'client';
        const userData = await createOrUpdateUserDocument(result.user, {
          displayName: credentials.displayName || '',
          role: role,
          emailVerified: result.user.emailVerified || false
        });
        
        if (userData) {
          // User document created, setting user state immediately
          setUser(userData);
          
          // Attempt auto-linking of application to this new user (client only)
          if (role === 'client' && result.user.email) {
            try {
              const findAndLink = httpsCallable(functions, 'findAndLinkApplicationByEmail');
              const linkResult = await findAndLink({});
              const linkedApplicationId = (linkResult.data as { linkedApplicationId?: string | null })?.linkedApplicationId;

              if (typeof linkedApplicationId === 'string' && linkedApplicationId) {
                // Refresh user to capture any auto-approval on link (pre-approved apps)
                // Add a short delay to ensure Firestore data has propagated
                await new Promise(resolve => setTimeout(resolve, 1000));

                await refreshUser();

                // Double-check if user is now approved
                const finalUserData = await getUserFromFirestore(result.user);
                if (finalUserData?.approved === true || finalUserData?.approvalStatus === 'approved') {
                  setUser(finalUserData);
                }
              }
            } catch (linkError) {
              // Non-blocking auto-linking failure
            }
          }
          
          if (role === 'admin' || role === 'super_admin') {
            // Admin signup completed successfully
            return { success: true };
          } else {
            // Client signup completed successfully! User needs approval
            return { success: true };
          }
        } else {
          // User document creation failed
          throw new Error('Failed to create user document');
        }
      }
      
      return { success: false, error: 'Signup failed' };
    } catch (error: any) {
      // Signup failed
      const code = error?.code || 'unknown';
      const errorMessage = getAuthErrorMessage(code);
      toast.error(errorMessage);
      return { success: false, error: errorMessage, errorCode: code };
    }
  };

  // Google login removed per product decision

  const logout = async () => {
    try {
      // CRITICAL FIX: Clean up user-specific storage before logout
      if (user?.uid) {
        // Remove user-specific localStorage items
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.includes(user.uid)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));

        // Remove user-specific sessionStorage items
        const sessionKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.includes(user.uid)) {
            sessionKeysToRemove.push(key);
          }
        }
        sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
      }

      await signOut(auth);
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error: any) {
      const errorMessage = 'Failed to logout. Please try again.';
      toast.error(errorMessage);
      throw error;
    }
  };

  const resetPassword = async (email: string, redirectPath?: string) => {
    try {
      const continueUrl = getPasswordResetContinueUrl(redirectPath);
      const actionCodeSettings: ActionCodeSettings = {
        url: continueUrl,
        handleCodeInApp: true
      };
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      toast.success('Password reset email sent. Please check your inbox.');
    } catch (error: any) {
      const errorMessage = getAuthErrorMessage(error.code);
      toast.error(errorMessage);
      throw error;
    }
  };

  const setUserRole = async (role: User['role'], pin?: string) => {
    if (!user) throw new Error('No user authenticated');
    
    try {
      setLoading(true);
      setError(null);

      // Admin PIN verification now happens via Cloud Function (verifyAdminPin)
      // before signup, so we don't need client-side PIN check here.
      // This function is only called after PIN has been verified server-side.

      // Update user role in Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { 
        role,
        roleAssignedAt: new Date()
      });

      // Update local user state
      setUser(prev => prev ? { ...prev, role } : null);
      
      const roleMessage = role === 'client' 
        ? 'Welcome to your business portal!'
        : 'Welcome to the VOLOCEAN admin portal!';
      
      toast.success(roleMessage);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to assign role. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async (retries = 8, delay = 250) => {
    if (!auth.currentUser) return;

    for (let i = 0; i < retries; i++) {
      try {
        const userData = await getUserFromFirestore(auth.currentUser);
        // Retry while Auth exists but Firestore user doc is not ready yet
        // (e.g. target_viewer claim: Admin SDK create lands after email-link sign-in).
        if (userData) {
          setUser(userData);
          return;
        }
      } catch {
        // retry below
      }
      if (i < retries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  };

  // Auth state + persistent user-doc listener.
  // Avoid cold getDoc() as the first Firestore op on a fresh WebChannel: under the
  // emulator (and intermittently in prod) that races target teardown into ca9, then
  // permanently bricks the client with b815. Anchor with onSnapshot instead.
  useEffect(() => {
    let userDocUnsub: (() => void) | undefined;
    let lastLoginTouchedForUid: string | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      userDocUnsub?.();
      userDocUnsub = undefined;

      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      let receivedFirstSnapshot = false;

      userDocUnsub = onSnapshot(
        doc(db, 'users', firebaseUser.uid),
        (snapshot) => {
          if (!snapshot.exists()) {
            if (!receivedFirstSnapshot) {
              receivedFirstSnapshot = true;
              setUser(null);
              setLoading(false);
            }
            return;
          }

          const userData = snapshot.data();

          setUser((prevUser) => {
            if (!prevUser || prevUser.uid !== firebaseUser.uid) {
              const role = userData.role as User['role'];
              const isTargetViewer = role === 'target_viewer';
              return {
                uid: firebaseUser.uid,
                email: firebaseUser.email || String(userData.email || ''),
                displayName:
                  firebaseUser.displayName || userData.displayName || '',
                role,
                createdAt:
                  (userData.createdAt as Timestamp) ||
                  Timestamp.fromDate(new Date()),
                lastLoginAt:
                  (userData.lastLoginAt as Timestamp) ||
                  Timestamp.fromDate(new Date()),
                organizationId: userData.organizationId,
                photoURL: userData.photoURL || null,
                bio: userData.bio || '',
                calendarUrl: userData.calendarUrl || null,
                outlookComposeSignatureHtml:
                  userData.outlookComposeSignatureHtml || null,
                outlookComplianceBccAddress: savedComplianceBccAddress(
                  userData.outlookComplianceBccAddress
                ),
                outlookComplianceBccUsOnly: Boolean(
                  userData.outlookComplianceBccUsOnly
                ),
                preferredCurrency:
                  userData.preferredCurrency || DEFAULT_CURRENCY,
                approved: userData.approved,
                approvalStatus: userData.approvalStatus,
                approvedBy: userData.approvedBy,
                approvedAt: userData.approvedAt,
                rejectionReason: userData.rejectionReason,
                clientId: isTargetViewer || userData.role === 'prospect_viewer'
                  ? undefined
                  : resolveUserLinkedApplicationId(userData as any) || undefined,
                linkedApplicationId: isTargetViewer || userData.role === 'prospect_viewer'
                  ? null
                  : (userData as any).linkedApplicationId || null,
                targetViewerClientId: isTargetViewer
                  ? typeof userData.targetViewerClientId === 'string'
                    ? userData.targetViewerClientId
                    : undefined
                  : undefined,
                targetViewerPreviewClientId:
                  !isTargetViewer &&
                  userData.role !== 'prospect_viewer' &&
                  typeof userData.targetViewerPreviewClientId === 'string'
                    ? userData.targetViewerPreviewClientId
                    : undefined,
                prospectViewerClientId:
                  typeof userData.prospectViewerClientId === 'string'
                    ? userData.prospectViewerClientId
                    : undefined,
                prospectViewerPreviewClientId:
                  !isTargetViewer &&
                  userData.role !== 'prospect_viewer' &&
                  typeof userData.prospectViewerPreviewClientId === 'string'
                    ? userData.prospectViewerPreviewClientId
                    : undefined,
              } as User;
            }

            const role = (userData.role as typeof prevUser.role) || prevUser.role;
            const isTargetViewer = role === 'target_viewer';
            const isProspectViewer = role === 'prospect_viewer';
            const isRestrictedViewer = isTargetViewer || isProspectViewer;

            return {
              ...prevUser,
              role,
              displayName: userData.displayName || prevUser.displayName,
              photoURL: userData.photoURL || null,
              bio: userData.bio || '',
              calendarUrl: userData.calendarUrl || null,
              outlookComposeSignatureHtml: userData.outlookComposeSignatureHtml || null,
              outlookComplianceBccAddress: savedComplianceBccAddress(
                userData.outlookComplianceBccAddress
              ),
              outlookComplianceBccUsOnly: Boolean(
                userData.outlookComplianceBccUsOnly
              ),
              approved: userData.approved,
              approvalStatus: userData.approvalStatus,
              approvedBy: userData.approvedBy,
              approvedAt: userData.approvedAt,
              rejectionReason: userData.rejectionReason,
              clientId: isRestrictedViewer
                ? undefined
                : resolveUserLinkedApplicationId({
                    ...prevUser,
                    ...userData,
                  } as any) || undefined,
              linkedApplicationId: isRestrictedViewer
                ? null
                : (userData as any).linkedApplicationId ||
                  (prevUser as any).linkedApplicationId ||
                  null,
              targetViewerClientId: isTargetViewer
                ? typeof userData.targetViewerClientId === 'string'
                  ? userData.targetViewerClientId
                  : prevUser.targetViewerClientId
                : undefined,
              targetViewerPreviewClientId: isRestrictedViewer
                ? undefined
                : typeof userData.targetViewerPreviewClientId === 'string'
                  ? userData.targetViewerPreviewClientId
                  : prevUser.targetViewerPreviewClientId,
              prospectViewerClientId:
                typeof userData.prospectViewerClientId === 'string'
                  ? userData.prospectViewerClientId
                  : undefined,
              prospectViewerPreviewClientId: isRestrictedViewer
                ? undefined
                : typeof userData.prospectViewerPreviewClientId === 'string'
                  ? userData.prospectViewerPreviewClientId
                  : prevUser.prospectViewerPreviewClientId,
            } as User;
          });

          if (!receivedFirstSnapshot) {
            receivedFirstSnapshot = true;
            setLoading(false);

            // Touch lastLoginAt only after the watch target is anchored (avoids
            // write + cold-getDoc race that triggers Firestore assertion ca9).
            if (lastLoginTouchedForUid !== firebaseUser.uid) {
              lastLoginTouchedForUid = firebaseUser.uid;
              // Typed fields only — Record<string, unknown> fails updateDoc UpdateData overload (TS2769).
              void updateDoc(doc(db, 'users', firebaseUser.uid), {
                lastLoginAt: serverTimestamp(),
                ...(firebaseUser.photoURL ? { photoURL: firebaseUser.photoURL } : {}),
                ...(firebaseUser.displayName
                  ? { displayName: firebaseUser.displayName }
                  : {}),
              }).catch(() => {
                // Non-blocking activity stamp
              });
            }
          }
        },
        () => {
          // Real-time listener error - fail closed on first load only
          if (!receivedFirstSnapshot) {
            receivedFirstSnapshot = true;
            setUser(null);
            setLoading(false);
          }
        }
      );
    });

    return () => {
      unsubscribeAuth();
      userDocUnsub?.();
    };
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    resetPassword,
    setUserRole,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 